import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from './components/DatePicker';
import TimeSelector from './components/TimeSelector';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import Head from '@/components/base/Head';
import { Link } from 'react-router-dom';
import { useAdminImage } from '@/hooks/useAdminImage';
import { supabase } from '@/lib/supabase';

export default function BookingPage() {
  const { t, i18n } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const heroBg = useAdminImage(
    'booking',
    'images.booking.hero_bg',
    'https://public.readdy.ai/ai/img_res/edited_6ae96513ab7d589359d8b6e225297343_9ced89c2.jpg'
  );

  const formatDisplayDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;

    setSubmitting(true);
    setSubmitError('');

    const formData = new FormData(e.currentTarget);

    const bookingData = {
      firstname: String(formData.get('firstname') || ''),
      lastname: String(formData.get('lastname') || ''),
      email: String(formData.get('email') || ''),
      phone: String(formData.get('phone') || ''),
      service: String(formData.get('service') || ''),
      package: String(formData.get('package') || ''),
      booking_date: selectedDate,
      booking_time: selectedTime,
      message: String(formData.get('message') || ''),
      status: 'new',
      source: 'website',
    };

    const { error } = await supabase.from('bookings').insert(bookingData);

    if (error) {
      setSubmitError(t('booking.error_message') || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
      setSubmitting(false);
      return;
    }

    // Send confirmation + clinic notification (best-effort; never blocks success)
    try {
      const edgeUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-booking-confirmation`;
      await fetch(edgeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ ...bookingData, language: i18n.language.split('-')[0] }),
      });
    } catch { /* email failure must not block the booking */ }

    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <>
      <Head
        title={t('seo.booking.title')}
        metas={[
          { name: 'description', content: t('seo.booking.description') },
          { name: 'keywords', content: t('seo.booking.keywords') },
          { property: 'og:title', content: t('seo.booking.title') },
          { property: 'og:description', content: t('seo.booking.description') },
          { property: 'og:url', content: 'https://estevaclinic.com/booking' },
          { property: 'og:type', content: 'website' },
          { property: 'og:locale', content: t('seo.ogLocale') },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: t('seo.booking.title') },
          { name: 'twitter:description', content: t('seo.booking.description') },
          { name: 'last-modified', content: '2026-06-08' },
        ]}
        links={[
          { rel: 'canonical', href: 'https://estevaclinic.com/booking' },
        ]}
      />
      <main className="bg-background-50 min-h-screen">
      <Navbar />

      <section className="relative pt-24 md:pt-32 pb-10 md:pb-14 px-6 md:px-10 bg-primary-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt={t('info.hero_image_alt')}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/60 to-primary-900/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <div className="mb-6 md:mb-8">
            <div className="inline-flex flex-col items-center">
              <span className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-[0.08em] leading-none">
                ESTEVA
              </span>
              <span className="font-label text-xs md:text-sm tracking-[0.35em] uppercase text-white/70 mt-1 md:mt-2">
                Clinic
              </span>
            </div>
          </div>
          <span className="inline-block px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/70 text-xs tracking-[0.2em] uppercase font-label">
            {t('booking.tag')}
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mt-4 leading-tight">
            {t('booking.heading_line1')}<br />
            <span className="italic font-light">{t('booking.heading_line2')}</span>
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/55 max-w-xl mx-auto font-body leading-relaxed">
            {t('booking.hero_subtitle')}
          </p>
        </div>
      </section>

      <section className="py-10 md:py-20 px-4 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-10">
            <div className="lg:col-span-2 space-y-4 md:space-y-6">
              <DatePicker selected={selectedDate} onChange={setSelectedDate} locale={i18n.language} />

              {selectedDate && (
                <div className="animate-in">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                      <i className="ri-calendar-check-line text-primary-600 text-sm"></i>
                    </div>
                    <span className="text-sm text-foreground-600 font-body">
                      {formatDisplayDate(selectedDate)}
                    </span>
                  </div>
                  <TimeSelector selected={selectedTime} onChange={setSelectedTime} />
                </div>
              )}

              {!selectedDate && (
                <div className="bg-background-100 rounded-2xl border border-dashed border-background-300 p-6 md:p-8 text-center">
                  <div className="w-12 h-12 md:w-14 md:h-14 mx-auto flex items-center justify-center rounded-full bg-primary-50">
                    <i className="ri-calendar-line text-xl md:text-2xl text-primary-400"></i>
                  </div>
                  <p className="mt-3 md:mt-4 text-xs md:text-sm text-foreground-400 font-body">
                    {t('booking.select_date_hint')}
                  </p>
                </div>
              )}
            </div>

            <div className="lg:col-span-3">
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-4 md:p-8">
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900">
                  {t('booking.your_data')}
                </h2>
                <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-foreground-500 font-body">
                  {t('booking.form_subtitle')}
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="mt-5 md:mt-7 space-y-4 md:space-y-5"
                >
                  <input type="hidden" name="date" value={selectedDate || ''} />
                  <input type="hidden" name="time" value={selectedTime || ''} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="booking-firstname" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                        {t('booking.firstname')}
                      </label>
                      <input
                        type="text"
                        id="booking-firstname"
                        name="firstname"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder={t('booking.firstname_placeholder')}
                      />
                    </div>
                    <div>
                      <label htmlFor="booking-lastname" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                        {t('booking.lastname')}
                      </label>
                      <input
                        type="text"
                        id="booking-lastname"
                        name="lastname"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder={t('booking.lastname_placeholder')}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="booking-email-ext" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                        {t('booking.email')}
                      </label>
                      <input
                        type="email"
                        id="booking-email-ext"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder={t('booking.email_placeholder')}
                      />
                    </div>
                    <div>
                      <label htmlFor="booking-phone-ext" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                        {t('booking.phone')}
                      </label>
                      <input
                        type="tel"
                        id="booking-phone-ext"
                        name="phone"
                        required
                        className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all"
                        placeholder={t('booking.phone_placeholder')}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="booking-service-ext" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                      {t('booking.service_label')}
                    </label>
                    <select
                      id="booking-service-ext"
                      name="service"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">{t('booking.service_placeholder')}</option>
                      <option value="dental">{t('services.dental.title')}</option>
                      <option value="surgery">{t('services.surgery.title')}</option>
                      <option value="combined">{t('booking.service_combined')}</option>
                      <option value="consultation">{t('booking.service_consultation')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="booking-package" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                      {t('booking.package_label')}
                    </label>
                    <select
                      id="booking-package"
                      name="package"
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">{t('booking.package_undecided')}</option>
                      <option value="essential">{t('booking.package_essential')}</option>
                      <option value="premium">{t('booking.package_premium')}</option>
                      <option value="luxe">{t('booking.package_luxe')}</option>
                      <option value="exclusif">{t('booking.package_exclusif')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="booking-message-ext" className="block text-xs font-label font-medium text-foreground-600 mb-1.5">
                      {t('booking.message_label')}
                    </label>
                    <textarea
                      id="booking-message-ext"
                      name="message"
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-lg border border-background-300 bg-background-100 text-sm text-foreground-800 font-body placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                      placeholder={t('booking.message_placeholder')}
                    ></textarea>
                    <p className="mt-1 text-xs text-foreground-400 font-body">{t('booking.max_chars')}</p>
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <input type="checkbox" name="privacy" required className="mt-0.5 accent-primary-500" />
                    <span className="text-xs text-foreground-500 font-body leading-relaxed group-hover:text-foreground-700 transition-colors">
                      {t('booking.privacy_consent')}
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={!selectedDate || !selectedTime || submitting}
                    className="w-full py-4 rounded-full bg-primary-800 text-white text-sm tracking-[0.12em] uppercase font-label font-semibold hover:bg-primary-700 disabled:bg-foreground-200 disabled:text-foreground-400 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        {t('booking.sending') || 'Wird gesendet...'}
                      </>
                    ) : (
                      <>
                        {t('booking.cta')}
                        <i className="ri-arrow-right-line text-lg"></i>
                      </>
                    )}
                  </button>

                  {(!selectedDate || !selectedTime) && (
                    <p className="text-xs text-amber-600 font-body text-center">
                      {t('booking.select_datetime_warning')}
                    </p>
                  )}

                  {submitError && (
                    <p className="text-xs text-red-600 font-body text-center">
                      {submitError}
                    </p>
                  )}
                </form>

                {submitted && (
                  <div className="mt-6 p-4 rounded-xl bg-accent-50 border border-accent-200 text-center">
                    <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100">
                      <i className="ri-check-line text-2xl text-accent-600"></i>
                    </div>
                    <p className="mt-3 text-sm font-label font-semibold text-accent-800">
                      {t('booking.success_title')}
                    </p>
                    <p className="mt-1 text-xs text-accent-600 font-body">
                      {t('booking.success_message')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-20 px-4 md:px-10 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs tracking-[0.2em] uppercase font-label">
              {t('booking.find_us')}
            </span>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mt-4">
              {t('booking.visit_us')}
            </h2>
            <p className="mt-2 text-sm text-foreground-500 font-body max-w-xl mx-auto">
              {t('booking.visit_subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1 bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8 space-y-5">
              <div>
                <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.address_label')}</p>
                <p className="text-sm text-foreground-800 font-body leading-relaxed">
                  Esteva Clinic<br />
                  Rruga Bylis<br />
                  Tiranë 1001<br />
                  {t('booking.albania_text')}
                </p>
              </div>

              <div className="border-t border-background-200 pt-5">
                <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.phone_label')}</p>
                <a href="tel:+41765593993" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-body cursor-pointer block">
                  +41 76 559 39 93
                </a>
              </div>

              <div className="border-t border-background-200 pt-5">
                <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-1">{t('imprint.email_label')}</p>
                <a href="mailto:info@esteva.ch" className="text-sm text-primary-600 hover:text-primary-700 transition-colors font-body cursor-pointer block">
                  info@esteva.ch
                </a>
              </div>

              <div className="border-t border-background-200 pt-5">
                <p className="text-[11px] tracking-[0.15em] uppercase text-foreground-400 font-label mb-2">{t('booking.opening_hours_label')}</p>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-600 font-body">{t('booking.day_mofr')}</span>
                    <span className="text-xs font-label font-semibold text-foreground-800">08:00 – 20:00</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-600 font-body">{t('booking.day_saturday')}</span>
                    <span className="text-xs font-label font-semibold text-foreground-800">09:00 – 16:00</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-foreground-600 font-body">{t('booking.day_sunday')}</span>
                    <span className="text-xs font-body italic text-foreground-400">{t('booking.day_closed')}</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-background-200 pt-5">
                <Link
                  to="/imprint"
                  className="inline-flex items-center gap-2 text-xs tracking-[0.1em] uppercase text-foreground-500 hover:text-primary-600 transition-colors font-label font-semibold cursor-pointer no-underline"
                >
                  {t('footer.imprint')}
                  <i className="ri-arrow-right-up-line"></i>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-background-200/70 h-[400px] md:h-[480px]">
              <iframe
                src="https://maps.google.com/maps?q=Rruga%20Bylis,%20Tiran%C3%AB%201001,%20Albania&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={false}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={t('info.map_title')}
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
    </>
  );
}